import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { 
  ShoppingBag, 
  Car, 
  Hotel, 
  Plane, 
  Utensils,
  ArrowRight,
  CheckCircle,
  ShoppingCart,
  Wrench,
  CircleDot,
  CarTaxiFront,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LanguageSelector } from '@/components/LanguageSelector';
import { buildPathForLocale, localizeInternalPath } from '@/lib/intl-seo';
import { PageSeo } from '@/components/seo/PageSeo';
import { HreflangLinks } from '@/components/seo/HreflangLinks';

const sellerCategories = [
  {
    id: 'products',
    icon: ShoppingBag,
    color: 'bg-blue-500',
    hoverColor: 'hover:bg-blue-600',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-200',
    path: '/seller?tab=products',
    features: ['seller.onboarding.unlimitedProducts', 'seller.onboarding.easyManagement', 'seller.onboarding.securePayments'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop'
  },
  {
    id: 'supermarket',
    icon: ShoppingCart,
    color: 'bg-teal-500',
    hoverColor: 'hover:bg-teal-600',
    lightBg: 'bg-teal-50',
    borderColor: 'border-teal-200',
    path: '/seller?tab=supermarket',
    features: ['seller.onboarding.readyCatalog', 'seller.onboarding.easyPricing', 'seller.onboarding.fastSetup'],
    image: 'https://images.unsplash.com/photo-1741010812421-2b5bff9f95fc?w=600&h=400&fit=crop'
  },
  {
    id: 'auto-parts',
    icon: Wrench,
    color: 'bg-slate-600',
    hoverColor: 'hover:bg-slate-700',
    lightBg: 'bg-slate-50',
    borderColor: 'border-slate-200',
    path: '/seller?tab=products&category=auto-parts',
    features: ['seller.onboarding.spareParts', 'seller.onboarding.wideInventory', 'seller.onboarding.vehicleCompatibility'],
    image: 'https://images.pexels.com/photos/4238222/pexels-photo-4238222.jpeg?w=600&h=400&fit=crop'
  },
  {
    id: 'tires',
    icon: CircleDot,
    color: 'bg-gray-700',
    hoverColor: 'hover:bg-gray-800',
    lightBg: 'bg-gray-50',
    borderColor: 'border-gray-200',
    path: '/seller?tab=products&category=tires',
    features: ['seller.onboarding.tireSales', 'seller.onboarding.appointmentService', 'seller.onboarding.sizeFilters'],
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop'
  },
  {
    id: 'cars',
    icon: Car,
    color: 'bg-green-500',
    hoverColor: 'hover:bg-green-600',
    lightBg: 'bg-green-50',
    borderColor: 'border-green-200',
    path: '/seller?tab=car-rentals',
    features: ['seller.onboarding.fleetManagement', 'seller.onboarding.availabilityCalendar', 'seller.onboarding.bookingSystem'],
    image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600&h=400&fit=crop'
  },
  {
    id: 'hotels',
    icon: Hotel,
    color: 'bg-purple-500',
    hoverColor: 'hover:bg-purple-600',
    lightBg: 'bg-purple-50',
    borderColor: 'border-purple-200',
    path: '/seller?tab=hotel-flights',
    features: ['seller.onboarding.roomManagement', 'seller.onboarding.reservationSystem', 'seller.onboarding.guestManagement'],
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop'
  },
  {
    id: 'flights',
    icon: Plane,
    color: 'bg-orange-500',
    hoverColor: 'hover:bg-orange-600',
    lightBg: 'bg-orange-50',
    borderColor: 'border-orange-200',
    path: '/seller?tab=hotel-flights',
    features: ['seller.onboarding.flightRoutes', 'seller.onboarding.ticketSales', 'seller.onboarding.passengerManagement'],
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=400&fit=crop'
  },
  {
    id: 'restaurants',
    icon: Utensils,
    color: 'bg-red-500',
    hoverColor: 'hover:bg-red-600',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-200',
    path: '/seller?tab=restaurants',
    features: ['seller.onboarding.menuManagement', 'seller.onboarding.onlineOrdering', 'seller.onboarding.deliverySystem'],
    image: 'https://images.pexels.com/photos/5779788/pexels-photo-5779788.jpeg?w=600&h=400&fit=crop'
  },
  {
    id: 'taxi-limousine',
    icon: CarTaxiFront,
    color: 'bg-yellow-500',
    hoverColor: 'hover:bg-yellow-600',
    lightBg: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    path: '/seller?tab=taxi-limousine',
    features: ['seller.onboarding.taxiFleet', 'seller.onboarding.limousineService', 'seller.onboarding.bookingManagement'],
    image: '/images/seller/taxi-limousine.jpg'
  }
];

const SellerOnboardingPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'tr';
  const lc = currentLang.substring(0, 2);
  const homePath = buildPathForLocale(lc, []);

  const handleCategoryClick = (category) => {
    const registerPath = `${buildPathForLocale(lc, ['seller-register'])}?category=${category.id}`;
    if (!user) {
      navigate(registerPath);
    } else if (user.role !== 'seller' && user.role !== 'admin') {
      navigate(registerPath);
    } else {
      navigate(localizeInternalPath(category.path, lc));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <HreflangLinks />
      <PageSeo
        title={t('seller.onboarding.title')}
        description={t('seller.onboarding.subtitle')}
        segments={['become-seller']}
      />
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Link to={homePath} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <img src="/logo.png" alt="Turkish E-Market" className="h-8 w-auto" />
            <span className="font-semibold text-xl text-primary">Turkish E-Market</span>
          </Link>
          
          <LanguageSelector />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('seller.onboarding.title')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t('seller.onboarding.subtitle')}
          </p>
        </div>

        {/* Category Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {sellerCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card 
                key={category.id}
                className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${category.borderColor} overflow-hidden group`}
                onClick={() => handleCategoryClick(category)}
              >
                <CardContent className="p-0">
                  {/* Category Image Header */}
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={t(`seller.onboarding.${category.id}Title`)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent`} />
                    
                    {/* Icon Badge */}
                    <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl ${category.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    
                    {/* Title on Image */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">
                        {t(`seller.onboarding.${category.id}Title`)}
                      </h3>
                      <p className="text-sm text-white/80">
                        {t(`seller.onboarding.${category.id}Desc`)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Features List */}
                  <div className="p-5">
                    <ul className="space-y-2 mb-4">
                      {category.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{t(feature)}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className={`w-full ${category.color} ${category.hoverColor} text-white rounded-full group-hover:gap-4 transition-all`}
                    >
                      {t('seller.onboarding.startNow')}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Info */}
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            {t('seller.onboarding.alreadySeller')}
          </p>
          <Link to={`${buildPathForLocale(lc, ['login'])}?tab=seller`}>
            <Button variant="outline" className="rounded-full">
              {t('seller.onboarding.loginToPanel')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SellerOnboardingPage;
